import { type FC, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { kyrgyzstanObjectBookingsStore } from 'entities/kyrgyzstan'

import { useInfiniteScroll } from 'shared/hooks/use-infinity-scroll'

import {
  type ObjectBookingsHistoryTab,
  objectBookingsHistoryTabs,
} from '../../model/types'
import { BookingCard } from '../booking-card'
import { BookingCardSkeleton } from '../booking-card-skeleton'

import s from './styles.module.scss'

interface Props {
  activeTab: ObjectBookingsHistoryTab
  onTabChange: (tab: ObjectBookingsHistoryTab) => void
}

const BOOKING_HISTORY_SKELETON_COUNT = 3

export const ObjectBookingsHistoryWidget: FC<Props> = observer(
  ({ activeTab, onTabChange }) => {
    const { t } = useTranslation()
    const paginationTargetRef = useRef<HTMLDivElement>(null)
    const {
      bookings,
      clearBookings,
      error,
      fetchBookings,
      hasBookings,
      hasMore,
      isLoading,
      loadMoreBookings,
      shouldShowEmptyState,
      shouldShowInitialLoader,
      shouldShowPaginationLoader,
    } = kyrgyzstanObjectBookingsStore

    useEffect(() => {
      void fetchBookings(activeTab === 'active')

      return () => {
        clearBookings()
      }
    }, [activeTab, clearBookings, fetchBookings])

    useInfiniteScroll({
      targetRef: paginationTargetRef,
      canLoad: hasBookings && hasMore && !isLoading && !error,
      onLoad: loadMoreBookings,
      trigger: hasBookings,
    })

    return (
      <section className={s.section}>
        <div
          className={s.tabs}
          role="tablist"
          aria-label={t(
            'kyrgyzstan_block.my_objects.bookings.history_page.title',
          )}
        >
          {objectBookingsHistoryTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={clsx('medium-15', s.tab, {
                [s.active]: activeTab === tab,
              })}
              onClick={() => onTabChange(tab)}
            >
              {t(
                `kyrgyzstan_block.my_objects.bookings.history_page.tabs.${tab}`,
              )}
            </button>
          ))}
        </div>

        <div className={s.list}>
          {shouldShowInitialLoader &&
            Array.from({ length: BOOKING_HISTORY_SKELETON_COUNT }).map(
              (_, index) => (
                <BookingCardSkeleton
                  key={index}
                  variant="full"
                />
              ),
            )}

          {bookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              variant="full"
            />
          ))}

          {shouldShowPaginationLoader && <BookingCardSkeleton variant="full" />}

          {shouldShowEmptyState && (
            <p className={clsx('medium-14', s.empty)}>
              {t(
                `kyrgyzstan_block.my_objects.bookings.history_page.empty.${activeTab}`,
              )}
            </p>
          )}

          <div
            ref={paginationTargetRef}
            className={s.paginationTarget}
          />
        </div>
      </section>
    )
  },
)
